import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InstagramAnalyticsListComments.graphql';
import { Query, QueryinstagramAnalyticsListCommentsArgs } from '../../__generated__/globalTypes';

export const useInstagramAnalyticsListCommentsQuery = (
  options?: QueryHookOptions<Query, QueryinstagramAnalyticsListCommentsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinstagramAnalyticsListCommentsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInstagramAnalyticsListCommentsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinstagramAnalyticsListCommentsArgs>
): QueryResult<Query, QueryinstagramAnalyticsListCommentsArgs> & {
  getInstagramAnalyticsListComments: LazyQueryExecFunction<Query, QueryinstagramAnalyticsListCommentsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInstagramAnalyticsListComments, result] = useLazyQuery<Query, QueryinstagramAnalyticsListCommentsArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getInstagramAnalyticsListComments, ...result };
};

export const useInstagramAnalyticsListCommentsPromiseQuery = () => {
  const client = useApolloClient();

  const getInstagramAnalyticsListComments = useCallback(
    (options?: Omit<QueryOptions<QueryinstagramAnalyticsListCommentsArgs, Query>, 'query'>) =>
      client.query<Query, QueryinstagramAnalyticsListCommentsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInstagramAnalyticsListComments };
};
// Suspense Query Hooks
export const useInstagramAnalyticsListCommentsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinstagramAnalyticsListCommentsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinstagramAnalyticsListCommentsArgs>(QUERY, {
    ...options
  });

  return result;
};
